import {
  type ReviewEvidence,
  type ReviewFindingRow,
  type ReviewScoutRow,
  type ReviewStage,
  type ReviewStreamRow,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowsMerge,
  CaretDown,
  CheckCircle,
  Circle,
  CircleNotch,
  MinusCircle,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatElapsed, REVIEW_ITEM_VISIBLE_WINDOW } from "./ReviewChecklist";
import {
  STATUS_DONE_ICON_CLASS,
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

/**
 * Live artwork-review progress while a v2 Deep review runs. Rembrandt writes
 * `chat.reviewStages` / `reviewStream`; Carrie polls and nests the flat stream
 * (scout, then its findings) inside the Review stage.
 *
 * Shares the collapsible status-panel shell with SearchProgress / ReviewChecklist
 * / ThinkingState via STATUS_PANEL_* so it reads as one surface in the thread.
 */

const REVIEW_STAGE_NAME = "review";
const READ_STAGE_NAME = "read";
const VERIFY_STAGE_NAME = "verify";

/** Shared with ReviewChecklist's overflow control so Deep matches Standard. */
const OVERFLOW_TOGGLE_CLASS = `${STATUS_PANEL_TOGGLE_MUTED_CLASS} rounded-lg ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`;

/** Stage rows stay a flat bordered chip; checks use the checklist card shell. */
const STAGE_ROW_CLASS =
  "flex w-full items-center gap-2 rounded-lg border border-puntt-neutral-gray-4 bg-base-white px-[11px] py-[7px]";

const FINDING_ROW_CLASS =
  "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-puntt-neutral-gray-3/90 focus-visible:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7";

const CHECK_CARD_CLASS =
  "overflow-hidden rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2";

const CHECK_TOGGLE_CLASS =
  "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-puntt-neutral-gray-3/90 focus-visible:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7";

type Tid = (name: string) => string;

export const DEEP_REVIEW_STAGE_NAMES = [
  "Prepare",
  "Read",
  "Review",
  "Verify",
  "Report",
  "Consolidate",
] as const;

/** Placeholder spine shown before rembrandt has written `reviewStages`. */
export const DEEP_REVIEW_PENDING_STAGES: ReviewStage[] =
  DEEP_REVIEW_STAGE_NAMES.map((name) => ({
    name,
    status: "pending",
    color: "gray",
  }));

export type CheckGroup = {
  check: ReviewScoutRow;
  findings: ReviewFindingRow[];
};

/** Group the flat producer→findings wire order into nested check trees. */
export function groupStreamByCheck(stream: ReviewStreamRow[]): CheckGroup[] {
  const groups: CheckGroup[] = [];
  let current: CheckGroup | null = null;
  for (const row of stream) {
    if (row.kind === "scout") {
      current = { check: row, findings: [] };
      groups.push(current);
      continue;
    }
    if (current) {
      current.findings.push(row);
      continue;
    }
    // why: some fixtures / older polls emit a finding before its scout
    current = {
      check: {
        kind: "scout",
        id: `_orphan-${row.id}`,
        label: "Findings",
        status: "done",
        draftCount: 1,
      },
      findings: [row],
    };
    groups.push(current);
  }
  return groups;
}

/** Drop pipeline jargon ("scout" / "pass") from the closed-row label. */
// why: `\bpass(?:es)?\b` strips "pass"/"passes" without eating "password".
export function displayCheckLabel(label: string): string {
  return label
    .replace(/\bscouts?\b/gi, "")
    .replace(/\bpass(?:es)?\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—:,]+|[\s\-–—:,]+$/g, "")
    .trim();
}

/**
 * Settle one stream row for a review that is over. Nothing may keep spinning
 * once the run has ended: the pipeline stops writing after "Report", so an
 * in-flight row would never resolve. A real verdict is preserved — a rejected
 * finding must not read as accepted, and a producer that finished stays done.
 */
function settleFinishedRow(row: ReviewStreamRow): ReviewStreamRow {
  if (row.kind === "scout") {
    // A producer still "running" in the last snapshot never reported a result
    // (crashed, or its terminal event never landed). Left alone it renders a
    // perpetual spinner + "Running" chip on a bar that says "Review complete".
    return row.status === "running" ? { ...row, status: "timed_out" } : row;
  }
  // Pre-verdict states all mean "still moving": a scout drafted it, the primary
  // emitted it, or an evidence agent was verifying it. Rembrandt keeps a draft
  // at `state: "draft"` when no verdict exists, so a run that ended before
  // triage left the row chipped "Checking" under "Review complete".
  const preVerdict =
    row.state === "draft" ||
    row.state === "emitted" ||
    row.state === "verifying";
  return preVerdict ? { ...row, state: "unresolved" } : row;
}

function StatusIcon({
  status,
  size = 14,
}: {
  status: ReviewScoutRow["status"];
  size?: number;
}) {
  if (status === "done") {
    return (
      <CheckCircle
        size={size}
        weight="fill"
        className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
        aria-hidden
      />
    );
  }
  if (status === "running") {
    return (
      <CircleNotch
        size={size}
        className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
        aria-hidden
      />
    );
  }
  // A crashed or timed-out producer used to render the same hollow circle as
  // one that had not started, so a review that lost a check looked identical to
  // one still queuing it. Labelled rather than aria-hidden: unlike the other
  // states, this one is not repeated in the row's text.
  if (status === "failed" || status === "timed_out") {
    return (
      <XCircle
        size={size}
        weight="fill"
        className="shrink-0 text-puntt-red-9"
        aria-label={status === "failed" ? "failed" : "timed out"}
      />
    );
  }
  if (status === "skipped") {
    return (
      <MinusCircle
        size={size}
        className="shrink-0 text-puntt-neutral-gray-8"
        aria-label="skipped"
      />
    );
  }
  return (
    <Circle
      size={size}
      className="shrink-0 text-puntt-neutral-gray-8"
      aria-hidden
    />
  );
}

function CheckClosedCount({
  group,
}: {
  group: CheckGroup;
}): { text: string; testIdSuffix: string } | null {
  // Resolved in CheckRow via t() — this only picks the case.
  if (group.check.status === "running" && group.findings.length === 0) {
    return { text: "running", testIdSuffix: "running" };
  }
  if (
    group.check.status === "failed" ||
    group.check.status === "timed_out" ||
    group.check.status === "skipped"
  ) {
    return { text: "could-not-run", testIdSuffix: "could-not-run" };
  }
  const count =
    group.findings.length > 0 ? group.findings.length : group.check.draftCount;
  if (count > 0) {
    return { text: String(count), testIdSuffix: "count" };
  }
  return null;
}

function CheckRow({
  group,
  tid,
  autoOpen,
}: {
  group: CheckGroup;
  tid: Tid;
  autoOpen: boolean;
}) {
  const { t } = useTranslation("ui");
  const { check, findings } = group;
  const expandable = findings.length > 0;
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const expanded = expandable && (manualToggle ?? autoOpen);
  const ran = check.status === "done" || check.status === "running";
  const label = displayCheckLabel(check.label) || check.label;
  const closed = CheckClosedCount({ group });
  const closedText =
    closed?.testIdSuffix === "running"
      ? t("reviewProgress.checkRunning")
      : closed?.testIdSuffix === "could-not-run"
        ? t("reviewProgress.checkCouldNotRun")
        : closed
          ? t("reviewProgress.findingCount", { count: Number(closed.text) })
          : null;

  const row = (
    <>
      <StatusIcon status={check.status} size={16} />
      <Text
        size="1"
        weight="medium"
        className={cx(
          "flex-1",
          ran ? "text-puntt-neutral-gray-12" : "text-puntt-neutral-gray-9",
        )}
      >
        {label}
      </Text>
      {closedText && (
        <Text
          size="1"
          data-testid={tid(`review-progress-scout-count-${check.id}`)}
          className="shrink-0 text-puntt-neutral-gray-9"
        >
          {closedText}
        </Text>
      )}
      {expandable && (
        <CaretDown
          size={12}
          aria-hidden
          className={cx(
            "shrink-0 transition-transform duration-200",
            ran ? "text-puntt-neutral-gray-12" : "text-puntt-neutral-gray-9",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        />
      )}
    </>
  );

  return (
    <div
      data-testid={tid(`review-progress-scout-${check.id}`)}
      className={CHECK_CARD_CLASS}
    >
      {expandable ? (
        <button
          type="button"
          data-testid={tid(`review-progress-scout-toggle-${check.id}`)}
          aria-expanded={expanded}
          aria-label={t("reviewProgress.checkToggleAriaLabel", {
            action: expanded
              ? t("reviewProgress.collapse")
              : t("reviewProgress.expand"),
            name: label,
          })}
          title={
            check.status === "done"
              ? undefined
              : t("reviewProgress.scoutStatusTitle", { status: check.status })
          }
          onClick={() => setManualToggle(!expanded)}
          className={CHECK_TOGGLE_CLASS}
        >
          {row}
        </button>
      ) : (
        <div
          className={CHECK_TOGGLE_CLASS}
          title={
            check.status === "done"
              ? undefined
              : t("reviewProgress.scoutStatusTitle", { status: check.status })
          }
        >
          {row}
        </div>
      )}
      {expanded && (
        <div className="flex flex-col gap-0.5 border-t border-puntt-neutral-gray-4 bg-base-white p-1.5">
          {findings.map((finding) => (
            <FindingRow key={finding.id} finding={finding} tid={tid} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Same rolling window + Show N earlier / Show less as ReviewChecklist. */
function CheckGroupList({ groups, tid }: { groups: CheckGroup[]; tid: Tid }) {
  const { t } = useTranslation("ui");
  const [showAll, setShowAll] = useState(false);
  const windowSize = REVIEW_ITEM_VISIBLE_WINDOW;
  const displayed = showAll
    ? groups
    : groups.slice(Math.max(0, groups.length - windowSize));
  const hiddenEarlierCount = showAll
    ? 0
    : Math.max(0, groups.length - windowSize);
  const hasMore = groups.length > windowSize;

  return (
    <div className="flex flex-col gap-1.5">
      {hasMore && hiddenEarlierCount > 0 && (
        <button
          type="button"
          data-testid={tid("review-progress-show-earlier")}
          aria-label={t("showEarlierItems")}
          onClick={(e) => {
            e.stopPropagation();
            setShowAll(true);
          }}
          className={OVERFLOW_TOGGLE_CLASS}
        >
          <Text size="1" className="text-puntt-neutral-gray-9">
            {t("reviewChecklist.showEarlierCount", {
              count: hiddenEarlierCount,
            })}
          </Text>
        </button>
      )}
      {displayed.map((group) => (
        <CheckRow
          key={group.check.id}
          group={group}
          tid={tid}
          // why: Lead reviewer stays "running" for the whole Review stage and
          // accumulates every primary finding — auto-opening it dumps the list.
          autoOpen={false}
        />
      ))}
      {hasMore && showAll && (
        <button
          type="button"
          data-testid={tid("review-progress-show-less")}
          aria-label={t("showFewerItems")}
          onClick={(e) => {
            e.stopPropagation();
            setShowAll(false);
          }}
          className={OVERFLOW_TOGGLE_CLASS}
        >
          <Text size="1" className="text-puntt-neutral-gray-9">
            {t("showLess")}
          </Text>
        </button>
      )}
    </div>
  );
}

const FINDING_STATE_LABEL_KEY: Record<ReviewFindingRow["state"], string> = {
  draft: "reviewProgress.findingState.drafted",
  emitted: "reviewProgress.findingState.queued",
  verifying: "reviewProgress.findingState.verifying",
  accepted: "reviewProgress.findingState.accepted",
  rejected: "reviewProgress.findingState.rejected",
  dismissed: "reviewProgress.findingState.dismissed",
  unresolved: "reviewProgress.findingState.unresolved",
  merged: "reviewProgress.findingState.merged",
};

function FindingStateChip({
  finding,
  tid,
}: {
  finding: ReviewFindingRow;
  tid: Tid;
}) {
  const { t } = useTranslation("ui");
  const { state } = finding;
  const label = t(FINDING_STATE_LABEL_KEY[state]);
  const isIssue = state === "accepted";
  const isCleared = state === "rejected" || state === "dismissed";
  return (
    <Text
      size="1"
      data-testid={tid(`review-progress-finding-state-${finding.id}`)}
      aria-label={t("reviewProgress.findingStateAriaLabel", {
        title: finding.title,
        label,
      })}
      className={cx(
        "flex shrink-0 items-center gap-1",
        isIssue
          ? "text-puntt-orange-9"
          : isCleared
            ? "text-malachite-700"
            : "text-puntt-neutral-gray-9",
      )}
    >
      {isIssue && <Warning size={12} weight="fill" aria-hidden />}
      {isCleared && <CheckCircle size={12} weight="fill" aria-hidden />}
      {state === "verifying" && (
        <CircleNotch
          size={12}
          className="animate-spin motion-reduce:animate-none"
          aria-hidden
        />
      )}
      {state === "merged" && <ArrowsMerge size={12} aria-hidden />}
      {label}
    </Text>
  );
}

function FindingRow({ finding, tid }: { finding: ReviewFindingRow; tid: Tid }) {
  const { t } = useTranslation("ui");
  // Per-row state: a lifted map would collapse the siblings whenever one row
  // opens, and the whole point is reading one finding while the rest stream in.
  const [expanded, setExpanded] = useState(false);
  const meta = [
    finding.area,
    finding.page != null
      ? t("reviewProgress.metaPage", { page: finding.page })
      : null,
  ].filter(Boolean);
  // why: chevron only when there is detail to open — title+chip alone is not a toggle.
  const hasDetail = meta.length > 0 || !!finding.description || !!finding.note;

  const rowBody = (
    <>
      {hasDetail && (
        <CaretDown
          size={12}
          aria-hidden
          className={cx(
            "shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        />
      )}
      <Text size="1" className="flex-1 text-puntt-neutral-gray-12">
        {finding.title}
      </Text>
      <FindingStateChip finding={finding} tid={tid} />
    </>
  );

  return (
    <div data-testid={tid(`review-progress-finding-${finding.id}`)}>
      {hasDetail ? (
        <button
          type="button"
          data-testid={tid(`review-progress-finding-toggle-${finding.id}`)}
          aria-expanded={expanded}
          aria-label={t("reviewProgress.findingToggleAriaLabel", {
            action: expanded
              ? t("reviewProgress.collapse")
              : t("reviewProgress.expand"),
            title: finding.title,
          })}
          onClick={() => setExpanded(!expanded)}
          className={FINDING_ROW_CLASS}
        >
          {rowBody}
        </button>
      ) : (
        <div className={FINDING_ROW_CLASS}>{rowBody}</div>
      )}
      {hasDetail && expanded && (
        <div
          data-testid={tid(`review-progress-finding-detail-${finding.id}`)}
          className="flex flex-col gap-0.5 px-2.5 pb-1.5 pl-8"
        >
          {meta.length > 0 && (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {meta.join(" · ")}
            </Text>
          )}
          {finding.description && (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {finding.description}
            </Text>
          )}
          {finding.note && (
            <Text size="1" className="text-puntt-neutral-gray-9">
              {finding.note}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

function WarningList({ warnings, tid }: { warnings: string[]; tid: Tid }) {
  return (
    <div
      data-testid={tid("review-progress-warnings")}
      className="flex flex-col gap-1"
    >
      {warnings.map((warning) => (
        <div key={warning} className="flex items-start gap-1.5">
          <Warning
            size={14}
            weight="fill"
            className="text-puntt-orange-9 mt-0.5 shrink-0"
            aria-hidden
          />
          <Text size="1" className="text-puntt-neutral-gray-11">
            {warning}
          </Text>
        </div>
      ))}
    </div>
  );
}

function StageRow({
  stage,
  tid,
  autoOpen,
  count,
  children,
}: {
  stage: ReviewStage;
  tid: Tid;
  autoOpen: boolean;
  count?: string | null;
  children?: ReactNode;
}) {
  const { t } = useTranslation("ui");
  const isDone = stage.status === "done";
  const isActive = stage.status === "active";
  const detail = stage.detail ?? [];
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const hasChildren = children != null;
  // A stage with nothing to report must not get a toggle that opens onto
  // nothing — Prepare never carries numbers.
  const expandable = detail.length > 0 || hasChildren;
  const expanded = expandable && (manualToggle ?? autoOpen);
  const key = stage.name.toLowerCase();
  // why: Review "N findings found" / "issues found, not an issue" duplicate the
  // check rows once the stream is nested under this stage.
  const showDetailLines =
    detail.length > 0 && !(key === REVIEW_STAGE_NAME && hasChildren);

  const row = (
    <>
      <StatusIcon status={isDone ? "done" : isActive ? "running" : "pending"} />
      <Text
        size="1"
        weight="medium"
        className={cx(
          "flex-1",
          isDone || isActive
            ? "text-puntt-neutral-gray-12"
            : "text-puntt-neutral-gray-9",
        )}
      >
        {stage.name}
      </Text>
      {count ? (
        <Text
          size="1"
          data-testid={tid(`review-progress-stage-count-${key}`)}
          className="shrink-0 text-puntt-neutral-gray-9"
        >
          {count}
        </Text>
      ) : null}
      {expandable && (
        <CaretDown
          size={12}
          aria-hidden
          className={cx(
            "shrink-0 transition-transform duration-200",
            isDone || isActive
              ? "text-puntt-neutral-gray-12"
              : "text-puntt-neutral-gray-9",
            expanded ? "rotate-0" : "-rotate-90",
          )}
        />
      )}
    </>
  );

  return (
    <div data-testid={tid(`review-progress-stage-${key}`)}>
      {expandable ? (
        <button
          type="button"
          data-testid={tid(`review-progress-stage-toggle-${key}`)}
          aria-expanded={expanded}
          aria-label={t("reviewProgress.stageToggleAriaLabel", {
            action: expanded
              ? t("reviewProgress.collapse")
              : t("reviewProgress.expand"),
            stageName: stage.name,
          })}
          onClick={() => setManualToggle(!expanded)}
          className={cx(STAGE_ROW_CLASS, "text-left")}
        >
          {row}
        </button>
      ) : (
        <div className={STAGE_ROW_CLASS}>{row}</div>
      )}
      {expanded && (
        <div
          data-testid={tid(`review-progress-stage-detail-${key}`)}
          className="flex flex-col gap-1.5 px-[11px] py-1"
        >
          {showDetailLines
            ? detail.map((line) => (
                <Text
                  key={line}
                  size="1"
                  className="text-puntt-neutral-gray-11"
                >
                  {line}
                </Text>
              ))
            : null}
          {children}
        </div>
      )}
    </div>
  );
}

function autoOpenStageKey(
  stages: ReviewStage[],
  collapseBodies: boolean,
): string | null {
  if (collapseBodies) return null;
  const active = stages.filter((s) => s.status === "active");
  const review = active.find((s) => s.name.toLowerCase() === REVIEW_STAGE_NAME);
  if (review) return REVIEW_STAGE_NAME;
  return active[0]?.name.toLowerCase() ?? null;
}

function stageClosedCount(opts: {
  stage: ReviewStage;
  groups: CheckGroup[];
  evidence?: ReviewEvidence;
  t: (key: string, values?: Record<string, unknown>) => string;
}): string | null {
  const key = opts.stage.name.toLowerCase();
  if (key === REVIEW_STAGE_NAME && opts.groups.length > 0) {
    return opts.t("reviewProgress.checkCount", { count: opts.groups.length });
  }
  if (key === VERIFY_STAGE_NAME && opts.evidence && opts.evidence.total > 0) {
    return opts.t("reviewProgress.confirmedText", {
      verified: opts.evidence.verified,
      total: opts.evidence.total,
    });
  }
  const first = opts.stage.detail?.[0];
  return first ?? null;
}

export function ReviewProgressBar({
  stages,
  evidence,
  stream,
  complete = false,
  finishingUp = false,
  allowPendingSpine = false,
  statusLabel,
  instanceId = "",
  startedAt,
  warnings,
}: {
  stages?: ReviewStage[];
  evidence?: ReviewEvidence;
  /** What the finished review could NOT do — an outlined pack whose OCR never
   * landed disables every wording check, and without saying so the result reads
   * as a clean pack. */
  warnings?: string[];
  /** Live scout/finding rows. Nested inside Review; order still carries hierarchy. */
  stream?: ReviewStreamRow[];
  /** When the review has finished, show every stage done regardless of the last
   * stage the backend wrote (the pipeline stops writing after "Report"). */
  complete?: boolean;
  /** Pipeline is done but the assistant summary is still being written. */
  finishingUp?: boolean;
  /** Render Prepare→Consolidate as pending when Mongo has not written stages yet. */
  allowPendingSpine?: boolean;
  /** Live phase sentence from `_PHASE_LABELS` (not "Reviewing — {stage}"). */
  statusLabel?: string | null;
  /** Suffix for every testid. The bar mounts twice when the artwork pane is
   * open, and two nodes per selector break Playwright's strict mode. */
  instanceId?: string;
  /** ISO start of the review turn — drives the elapsed clock. A stage can hold
   * for many minutes; a moving number is what says the run is alive. */
  startedAt?: string | null;
}) {
  const { t } = useTranslation("ui");
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);

  const pendingSpine = allowPendingSpine && (!stages || stages.length === 0);
  const sourceStages = pendingSpine
    ? DEEP_REVIEW_PENDING_STAGES
    : (stages ?? []);

  const allStagesDone =
    !pendingSpine &&
    sourceStages.length > 0 &&
    sourceStages.every((s) => s.status === "done");
  const pipelineDone = complete || allStagesDone;
  const trulyFinished = pipelineDone && !finishingUp && !pendingSpine;

  const ticking = !!startedAt && !trulyFinished;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ticking]);

  if (sourceStages.length === 0) return null;

  const tid: Tid = (name) => (instanceId ? `${name}-${instanceId}` : name);

  // The pipeline stops writing `reviewStages` AND `reviewStream` at the same
  // moment — including through `finishingUp`, the post-pipeline window where
  // the assistant is still streaming its write-up. Stages and stream have to
  // agree about that: settling only the stages left an all-green spine beside a
  // producer still spinning "Running" and findings still chipped "Confirming",
  // for as long as the summary took to write.
  const pipelineSettled = (pipelineDone || finishingUp) && !pendingSpine;

  const displayStages: ReviewStage[] = pipelineSettled
    ? sourceStages.map((s) => ({ ...s, status: "done", color: "green" }))
    : sourceStages;

  const displayStream: ReviewStreamRow[] = pipelineSettled
    ? (stream ?? []).map(settleFinishedRow)
    : (stream ?? []);

  const checkGroups = groupStreamByCheck(displayStream);
  const hasReviewStage = displayStages.some(
    (stage) => stage.name.toLowerCase() === REVIEW_STAGE_NAME,
  );
  const readWarnings =
    !!warnings?.length &&
    displayStages.some((s) => s.name.toLowerCase() === READ_STAGE_NAME)
      ? warnings
      : undefined;
  const fallbackWarnings =
    !!warnings?.length && !readWarnings ? warnings : undefined;

  const collapseBodies = trulyFinished || finishingUp || pendingSpine;
  const openStageKey = autoOpenStageKey(displayStages, collapseBodies);

  const inFlight = !trulyFinished;
  // why: once the run is done, collapse the stage spine so later chat updates
  // have room — same default as ReviewChecklist after reviewComplete.
  const expanded = manualToggle !== null ? manualToggle : !trulyFinished;

  const trimmedStatus = statusLabel?.trim();
  const summaryText = trulyFinished
    ? t("reviewProgress.complete")
    : finishingUp
      ? t("reviewProgress.writingSummary")
      : trimmedStatus
        ? trimmedStatus
        : t("reviewProgress.preparing");
  const elapsedLabel = trulyFinished ? null : formatElapsed(startedAt, now);

  return (
    <div className="my-3">
      <div
        data-testid={tid("review-progress-bar")}
        role="status"
        aria-live="polite"
        aria-label={t("reviewProgress.ariaLabel")}
        className={cx(
          "overflow-hidden",
          interactiveSurfaceClasses({
            expandable: true,
            muted: true,
            open: expanded,
          }),
        )}
      >
        <button
          data-testid={tid("review-progress-toggle")}
          type="button"
          aria-expanded={expanded}
          aria-label={t("reviewProgress.barToggleAriaLabel", {
            action: expanded
              ? t("reviewProgress.collapse")
              : t("reviewProgress.expand"),
          })}
          onClick={() => setManualToggle(!expanded)}
          // why: the shell is `muted`, so interactiveSurfaceClasses leaves
          // hoverFlat undefined -- without the token this open accordion is
          // the only one of the family (AgentSteps / SearchProgress /
          // EnterpriseImpact / FeedMemoryCard) with no hover affordance.
          className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
        >
          {inFlight ? (
            <CircleNotch
              size={16}
              className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <CheckCircle
              size={16}
              weight="fill"
              className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
              aria-hidden
            />
          )}
          <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            <div className="flex w-full items-center gap-2">
              <Text
                size="1"
                weight="medium"
                className="text-puntt-neutral-gray-12"
              >
                {summaryText}
              </Text>
              {elapsedLabel && (
                <Text
                  size="1"
                  data-testid={tid("review-progress-elapsed")}
                  className="shrink-0 whitespace-nowrap text-puntt-neutral-gray-9"
                >
                  {elapsedLabel}
                </Text>
              )}
            </div>
            {inFlight && (
              <Text
                size="1"
                data-testid={tid("review-progress-duration")}
                className="text-puntt-neutral-gray-9"
              >
                {t("reviewProgress.typicalDuration")}
              </Text>
            )}
          </div>
          <CaretDown
            size={14}
            aria-hidden
            className={cx(
              "ml-auto shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200",
              expanded ? "rotate-0" : "-rotate-90",
            )}
          />
        </button>

        {expanded && (
          <div
            data-testid={tid("review-progress-items")}
            // The rows re-render on every 3s poll and can run to dozens; inside
            // the panel's aria-live="polite" a screen reader would read the
            // whole list out again each time. The summary above stays live.
            aria-live="off"
            className={STATUS_PANEL_ITEMS_MUTED_CLASS}
          >
            <div className="flex flex-col gap-1.5">
              {displayStages.map((stage) => {
                const key = stage.name.toLowerCase();
                const isReview = key === REVIEW_STAGE_NAME;
                const isRead = key === READ_STAGE_NAME;
                const nestedChecks = isReview ? checkGroups : [];
                const stageWarnings = isRead ? readWarnings : undefined;
                const hasWarnings = !!stageWarnings?.length;
                const hasNested = nestedChecks.length > 0 || hasWarnings;
                return (
                  <StageRow
                    key={stage.name}
                    stage={stage}
                    tid={tid}
                    // why: rembrandt always writes the full Prepare→Consolidate
                    // spine, so warnings always nest under "Read" in
                    // production. A finished run collapses every stage body,
                    // which buried "we never read this pack" behind a second
                    // click on an all-green bar — the exact failure `warnings`
                    // exists to prevent. A stage holding notes opens itself.
                    autoOpen={openStageKey === key || hasWarnings}
                    count={stageClosedCount({
                      stage,
                      groups: checkGroups,
                      evidence,
                      t,
                    })}
                  >
                    {hasNested ? (
                      <>
                        {nestedChecks.length > 0 ? (
                          <CheckGroupList groups={nestedChecks} tid={tid} />
                        ) : null}
                        {stageWarnings ? (
                          <WarningList warnings={stageWarnings} tid={tid} />
                        ) : null}
                      </>
                    ) : null}
                  </StageRow>
                );
              })}
              {/* No stage named "Review": keep grouped checks on screen. */}
              {!hasReviewStage && checkGroups.length > 0 ? (
                <CheckGroupList groups={checkGroups} tid={tid} />
              ) : null}
              {fallbackWarnings ? (
                <WarningList warnings={fallbackWarnings} tid={tid} />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
